import { useEffect, useMemo, useState } from 'react';
import { ModalShell } from '../design/ModalShell';
import { DataTable } from '../design/DataTable';
import { SearchInput } from '../design/SearchInput';
import { Button } from '../design/Button';
import { formatRelative } from '../design/FlowPickerReskin';
import {
  apiPost,
  type CloudFlowSummary,
  type ClassifyTriggerResult,
  type FlowTriggerType,
} from '../../api';

/**
 * Tool Upload P1 — "Select one of your Automations"
 *
 * Step 0 of the upload wizard: the picker that runs before the four numbered
 * steps. It draws NO step pills, which is why it is not "step 1" — the pills on
 * P2–P5 start their count at "Describe your tool". See StepPills.
 *
 * MEASURED from `Tool Upload P1.svg`, all offsets relative to the modal's true
 * top-left of (190, 231) rather than the canvas:
 *
 *   modal box            190,231  1056x640  r16      (identical on P1–P5)
 *   title baseline       287.227  24px/300            -> cap-top ~269.8
 *   "Upload from device" 1070.5,271.5  142x31  r7.5 outlined
 *   header row inset     ~40 from modal top          (--space-12)
 *   search bar           222,322  792x23  r8  --accent-subtle (teal @ 0.1)
 *   "Filter"             1163.5,322.5  49x22  r7.5 outlined
 *   table header top     361                          -> 39 below the search bar
 *   table rows           361..426, 426..491, 490..556, 556..621  (contiguous)
 *   footer rule          191,782  1054x1  --ink       (P1 ONLY)
 *   footer buttons       y 815.5..837.5, h22 r7.5 outlined
 *   modal bottom         871
 *
 * HORIZONTAL INSETS. Title at x=221 (31 from the modal edge), search bar and
 * table at x=222 (32), footer's left button at 222.5. These are one 31.5px inset
 * (--modal-pad-x) plus the half-pixel stroke offset, not three values — the same
 * collapse documented on the modal box itself. The footer's right edge is the one
 * genuine exception: "Next" ends at 1216.5 against a modal right edge of 1246, an
 * inset of ~29 (--modal-footer-inset-right), so the footer is inset slightly more
 * on the right than the body is.
 *
 * THE 161px GAP. The table's last row ends at 621 and the footer rule is at 782,
 * leaving 161px empty. That is not a spacing value — it is room for more rows
 * than the frame happens to draw. So the table region is a flexible column that
 * fills the space, and the footer is pinned to the bottom of the modal, rather
 * than the gap being reproduced as padding. With three rows the result matches
 * the frame; with eight it scrolls instead of overflowing.
 *
 * WHAT IS DELIBERATELY NOT HERE:
 *   - No step pills (the frame draws none).
 *   - No modal header rule (no frame draws one; see ModalShell).
 *   - No sort interaction. The header's sort mark is drawn, but no sorted state,
 *     no direction indicator and no second column's mark exist anywhere in the
 *     eleven files, so wiring it would be inventing behaviour. See audit Q7.
 *
 * WHAT CHANGED IN THE BACKEND COPY. Layout, classNames and every measurement above are
 * untouched. Only data and handlers — and this page's own TODO(dev) (just above, in the
 * Design's words: "Wire these to the wizard's real navigation and to the user's
 * Automations list") is what got done:
 *
 *   1. THE `automations` FIXTURE -> the builder's REAL cloud flows, passed in as `flows`
 *      (`/api/flows/mine`, already loaded by the Share loader — no new call). The search
 *      still filters client-side, which is now correct rather than a stand-in: the whole
 *      list is already in memory, so a server round-trip per keystroke would be slower
 *      and no more accurate.
 *   2. THE COLUMNS. `Name` is the flow's own name. `Modified` is its `modifiedOn`
 *      (Dataverse `modifiedon`) run through `formatRelative` — the design draws a
 *      relative string ("2 wk ago"), and that conversion already exists in
 *      FlowPickerReskin, so it is imported rather than rewritten. `Type` is Power
 *      Automate's Automated/Instant/Scheduled trigger classification, which needs one
 *      fetch per flow, so it LOADS LAZILY: the table opens with Type blank and each cell
 *      fills as its request resolves. All three are the same sources FlowPickerReskin
 *      already reads, because this page REPLACES that picker.
 *   3. "Last Uploaded" HAS NO SOURCE and is not faked. The sheet draws the column, but
 *      the backend records creation, not upload, per flow — so the ported `DataTable`
 *      dropped it (its own comment says "minus Last Uploaded") and its grid is three
 *      columns. Inventing a date here would be inventing data.
 *   4. `navigate('/upload/2-describe')` -> `onPick(flow)`, which hands the chosen
 *      automation to the overlay's own state and swaps this frame for P2. `Cancel` ->
 *      `onClose`. Both are the same gestures with no router.
 *   5. "Continue Draft" resumes the ONE latest unfinished upload — the "changed my mind"
 *      way back in. There is only ever one draft (the single-draft model — see
 *      draftStore.latestDraft / clearOtherDrafts), and the "continue or start new?" choice is
 *      already offered BEFORE this picker (see UploadOverlayPages' gate). So this button is for the
 *      builder who chose "start new", reached the picker, then realised they did want their draft:
 *      it is enabled whenever a draft EXISTS, independent of the selected row, because the draft is
 *      not about the selected automation. `onContinueDraft` resumes it; the destination step is the
 *      journey's own concern (the hook restores where the draft left off). It is the design's
 *      disabled state when there is no draft to continue. This answers its old TODO — "should resume
 *      the most recent unfinished DRAFT rather than start fresh" — without guessing a landing step.
 *
 * STILL TODO(dev), unchanged from the Design: "Upload from device" and "Filter" have no
 * handlers — the design specifies no file-picker flow and draws no filter panel, so what
 * they do is unspecified. Sort is likewise still not wired (see above).
 */
export interface UploadSelectPageProps {
  /** The builder's real cloud flows — the rows this table lists. */
  flows: CloudFlowSummary[];
  /** The environment those flows live in; sent with each classify-trigger request. */
  environmentId: string;
  /**
   * Whether a resumable draft exists at all. This is what makes "Continue Draft" honest: there is
   * only ever ONE draft — the latest unfinished upload — and this button is the "changed my mind"
   * way back into it. So it is enabled whenever a draft exists, INDEPENDENT of which row is selected
   * (the draft is not about the selected automation; it is the last upload the builder was making).
   * False ⇒ nothing to continue ⇒ the button is the design's disabled state.
   */
  hasDraft: boolean;
  /** Resume the one latest draft — the "changed my mind" action, independent of the selected row. */
  onContinueDraft: () => void;
  /** A row was chosen and confirmed — hand it to the wizard (replaces `navigate`). */
  onPick: (flow: CloudFlowSummary) => void;
  /**
   * UP-F04 entry: "Upload from device" — switches the overlay into the PARALLEL artefact flow
   * (File / Prompt / Link), which has no cloud automation. Wired by UploadOverlayPages; the button
   * was a TODO(dev) stub before.
   */
  onUploadFromDevice: () => void;
  /** Dismiss the overlay (Cancel). */
  onClose: () => void;
}

/** How many classify-trigger fetches run at once — keep Dataverse pressure modest. */
const CLASSIFY_CONCURRENCY = 5;

export function UploadSelectPage({
  flows,
  environmentId,
  hasDraft,
  onContinueDraft,
  onPick,
  onUploadFromDevice,
  onClose,
}: UploadSelectPageProps) {
  // The search bar filters the automations list in the browser (user request).
  // Case-insensitive substring match across the visible text columns — Name,
  // Type and the two date/relative strings — so typing narrows the table as the
  // frame's own list would. Client-side is now the RIGHT answer, not a stand-in:
  // the full list is already in memory (see note 1).
  const [query, setQuery] = useState('');

  // Which automation is selected, lifted out of DataTable (which already reports
  // it via onSelect, null when the row is toggled off). Next is gated on this:
  // you cannot advance until you have picked a version (user request). The
  // design draws a `disabled` button state (Buttons.svg — grey label, per
  // Button.tsx), so the greyed-out Next is a MEASURED state, not an invention.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /** Lazily-filled Type per workflowId. Absent = still loading (cell shows '—'). */
  const [triggerTypes, setTriggerTypes] = useState<Map<string, FlowTriggerType>>(new Map());

  // One clock read for the whole render pass, so every "N h ago" is measured against the
  // same instant. Read via an effect, not in render: Date.now() is impure, and calling it
  // during render trips the React purity rule. Lazy-initialised once at mount, then re-read
  // when the flows list identity changes.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    setNowMs(Date.now());
  }, [flows]);

  // ── Lazy Type: classify every listed flow, capped concurrency, out-of-order-safe ──
  //
  // Fires once per mount over the FULL flows list (not the filtered view) so a later
  // search never re-triggers fetches. Each result merges in as it lands; the cleanup flag
  // drops any reply arriving after unmount. Same construction as FlowPickerReskin's.
  useEffect(() => {
    let alive = true;
    const queue = flows.map((f) => f.workflowId);
    let cursor = 0;

    async function worker() {
      while (alive && cursor < queue.length) {
        const id = queue[cursor++];
        if (!id) continue;
        const res = await apiPost<ClassifyTriggerResult>('/api/tools/classify-trigger', {
          targetEnvironmentId: environmentId,
          workflowId: id,
        });
        if (!alive) return;
        if (res.kind === 'ok') {
          const triggerType = res.body.triggerType;
          setTriggerTypes((prev) => new Map(prev).set(id, triggerType));
        }
      }
    }

    void Promise.all(Array.from({ length: Math.min(CLASSIFY_CONCURRENCY, queue.length) }, worker));
    return () => {
      alive = false;
    };
  }, [flows, environmentId]);

  // The real flows as table rows, then the search filter over them. `modified` is the
  // design's relative string; `type` is blank until its classify request resolves, and
  // 'Unknown' (an unreadable blueprint) also reads blank rather than as a value.
  const rows = useMemo(() => {
    const all = flows.map((flow) => {
      const t = triggerTypes.get(flow.workflowId);
      return {
        id: flow.workflowId,
        name: flow.name,
        modified: formatRelative(flow.modifiedOn ?? null, nowMs),
        type: t && t !== 'Unknown' ? t : '—',
      };
    });
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((row) =>
      [row.name, row.type, row.modified].join(' ').toLowerCase().includes(q),
    );
  }, [flows, triggerTypes, nowMs, query]);

  /** The selected row's flow, or null. Next/Continue Draft are gated on this. */
  const selectedFlow = useMemo(
    () => flows.find((f) => f.workflowId === selectedId) ?? null,
    [flows, selectedId],
  );

  return (
    <ModalShell
      labelledBy="upload-select-title"
      headerInset="title"
      header={
        // Title and "Upload from device" share one row: the title's cap-top
        // (~269.8) and the button's top (271.5) are within 2px, so they are
        // optically centred against each other rather than baseline-aligned.
        // `items-center` on a row whose tallest child is the 31px button
        // reproduces that without either value being written down.
        <div className="flex flex-wrap items-center justify-between gap-7">
          <h1 id="upload-select-title" className="min-w-0 text-xl font-light text-text">
            Select one of your Automations
          </h1>
          {/* UP-F04: enters the parallel artefact flow (File / Prompt / Link) — no cloud
              automation, so it is NOT a row pick. See onUploadFromDevice. */}
          <Button
            variant="outline-square"
            size="sm"
            className="shrink-0"
            onClick={onUploadFromDevice}
          >
            Upload from device
          </Button>
        </div>
      }
      footer={
        // The rule is passed as part of the footer because it exists on P1 and
        // on no other upload frame — see ModalShell's "what is not shared".
        <div className="border-t border-ink">
          <div className="flex flex-wrap items-center justify-between gap-7 px-modal-x py-11 pr-modal-footer-r">
            {/* The "changed my mind" button: resumes the ONE latest unfinished upload (see note 5).
                Enabled whenever a draft EXISTS — independent of the selected row, because the draft
                is not about the selected automation. Disabled (the design's grey state) when there
                is nothing to continue. The onClick is guarded too, so a disabled click cannot fire. */}
            <Button
              variant="outline-square"
              size="xs"
              state={hasDraft ? 'default' : 'disabled'}
              onClick={() => {
                if (hasDraft) onContinueDraft();
              }}
            >
              Continue Draft
            </Button>
            <div className="flex items-center gap-modal-footer">
              {/* Dismisses the wizard — the design's only dismiss affordance
                  (there is no X on any frame). Returns to the uploads list.
                  TODO(dev): if a draft is in progress, confirm-before-discard
                  is a backend/UX concern the frames do not specify. */}
              <Button variant="outline-square" size="xs" onClick={onClose}>
                Cancel
              </Button>
              {/* Advances to wizard step 1 — but only once a row is selected.
                  Until then it is the design's `disabled` state (grey label,
                  no-op), so the user must pick a version first (user request).
                  The onClick is guarded too, so the disabled button cannot
                  navigate even if a click gets through. */}
              <Button
                variant="outline-square"
                size="xs"
                state={selectedFlow == null ? 'disabled' : 'default'}
                onClick={() => {
                  if (selectedFlow) onPick(selectedFlow);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      }
    >
      {/* Search row. The bar runs 222..1014 and "Filter" 1163.5..1212.5, so
          the bar does NOT stretch to meet the button — there is a 149px gap
          the design leaves empty. Reproduced as a flexible bar plus a fixed
          button, which closes that gap at 1440 and keeps both usable when the
          modal is narrower. Logged as a deviation rather than reproduced,
          because a 149px hole is not a spacing value. */}
      <div className="flex items-center gap-7 pt-11">
        <SearchInput size="xs" block value={query} onChange={setQuery} />
        {/* TODO(dev): opens filter controls. The design draws no filter panel,
            so what this reveals is unspecified. */}
        <Button variant="outline-square" size="xs" className="shrink-0">
          Filter
        </Button>
      </div>

      {/* No row is selected on load (user request): the table starts empty,
          hovering a row shows the teal highlight, and clicking fills its dot.
          Selection is single and owned inside DataTable. Rows are the
          search-filtered list — see `rows` above. */}
      <div className="pt-11 pb-11">
        <DataTable rows={rows} onSelect={setSelectedId} />
      </div>
    </ModalShell>
  );
}
